

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { addCsrfHeader } from '../utils/csrfUtils';


//this hook will return the current subscription details for the user (stored on in pgUser)
//and it will return any scheduled subscriptions for the user (fetched from stripe)
export const useStripeDetails = () => {
    const { sessionToken, pgUser } = useAuth();

    const [stripeDetails, setStripeDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStripeSubscriptionDetails = async () => {

        console.log('fetchStripeSubscriptionDetails called');

        //helper function to convert epoch to date (June 21, 2025)
        const epochToDate = (epoch) => {
            const date = new Date(epoch * 1000);
            return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        }
        
        console.log('pgUser:', pgUser);
        
        try {
            let stripeObject = {
                currentSubscription: {
                    
                },
                scheduledSubscription: {
                    
                }
            };

            console.log('fetching active subscription from stripe');
            //get active subscription from stripe
            const activeSubscription = await fetch(`/api/v1/users/stripe_active_subscriptions`, {
                method: 'POST',
                headers: await addCsrfHeader({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                }),
                body: JSON.stringify({ app_user_id: pgUser.id })
            });

            if (!activeSubscription.ok) {
                throw new Error('Failed to fetch active subscription');
            }
            console.log('made it here1');

            const activeSubscriptionData = await activeSubscription.json();
            console.log('activeSubscriptionData:', activeSubscriptionData);
            
            if (!Array.isArray(activeSubscriptionData)) {
                console.error('Expected array but got:', typeof activeSubscriptionData);
                throw new Error('Invalid subscription data format');
            }
            
            // Since activeSubscriptionData is an array, get the first item
            const currentSubscription = activeSubscriptionData[0];
            console.log('currentSubscription:', currentSubscription);
            
            if (currentSubscription) {
                stripeObject.currentSubscription = {
                    id: currentSubscription.id,
                    price_id: currentSubscription.plan.id,
                    cancel_at_period_end: currentSubscription.cancel_at_period_end,
                    cancel_at: currentSubscription.cancel_at ? epochToDate(currentSubscription.cancel_at) : null,
                    status: currentSubscription.status,
                    renewal_date: currentSubscription.items?.data?.[0]?.current_period_end ? epochToDate(currentSubscription.items.data[0].current_period_end) : null
                };
                console.log('Current subscription set:', stripeObject.currentSubscription);
            } else {
                console.log('No active subscription found');
                stripeObject.currentSubscription = null;
            }
            console.log('made it here2');

            console.log('fetching scheduled subscriptions from stripe with sessionToken:', sessionToken);
            //fetch scheduled subscriptions from stripe
            const scheduledSubscriptions = await fetch(`/api/v1/users/stripe_subscription_schedules`, {
                method: 'POST',
                headers: await addCsrfHeader({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                }),
                body: JSON.stringify({ app_user_id: pgUser.id })
            });

            if (!scheduledSubscriptions.ok) {
                throw new Error('Failed to fetch scheduled subscriptions');
            }

            const scheduledSubscriptionsData = await scheduledSubscriptions.json();
            console.log('scheduledSubscriptionsData:', scheduledSubscriptionsData);

            // Only set scheduled subscription if we have valid data with a start date
            if ((scheduledSubscriptionsData?.status === 'active' || scheduledSubscriptionsData?.status === 'not_started') && 
                scheduledSubscriptionsData.phases?.[0]?.start_date) {
                try {
                    const startDate = epochToDate(scheduledSubscriptionsData.phases[0].start_date);
                    const priceId = scheduledSubscriptionsData.phases?.[0]?.items?.[0]?.price;
                    
                    if (startDate && priceId) {
                        stripeObject.scheduledSubscription = {
                            id: scheduledSubscriptionsData.id,
                            start_date: startDate,
                            price_id: priceId,
                            status: scheduledSubscriptionsData.status
                        };
                        console.log('Set scheduled subscription:', stripeObject.scheduledSubscription);
                    } else {
                        console.log('Missing required data for scheduled subscription:', { startDate, priceId });
                        stripeObject.scheduledSubscription = null;
                    }
                } catch (err) {
                    console.error('Error processing scheduled subscription data:', err);
                    stripeObject.scheduledSubscription = null;
                }
            } else {
                console.log('No valid scheduled subscription found:', { 
                    status: scheduledSubscriptionsData?.status,
                    hasStartDate: Boolean(scheduledSubscriptionsData?.phases?.[0]?.start_date)
                });
                stripeObject.scheduledSubscription = null;
            }

            console.log('stripeObject:', stripeObject);


            setStripeDetails(stripeObject);
            setLoading(false);
            setError(null);
            return stripeObject;
        } catch (err) {
            setError(err.message);
            setLoading(false);
            return null;
        }
    };

    return { fetchStripeSubscriptionDetails, loading, error };
}

export const useModifySubscription = () => {
    const { sessionToken, pgUser } = useAuth();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const pgUserId = pgUser.id;

    const modifySubscription = async (new_price_id) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`/api/v1/users/switch_stripe_subscription`, {
                method: 'POST',
                headers: await addCsrfHeader({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                }),
                body: JSON.stringify({ app_user_id: pgUserId, new_price_id })
            });

            if (!response.ok) {
                throw new Error('Failed to modify subscription');
            }
            
        } catch (error) {
            setError('Failed to modify subscription. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    return { modifySubscription, loading, error };
}

export const useCancelCurrentSubscription = () => {
    const { sessionToken, pgUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const pgUserId = pgUser.id;


    const cancelAllSubscriptions = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`/api/v1/users/cancel_stripe_subscription_all`, {
                method: 'POST',
                headers: await addCsrfHeader({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                }),
                body: JSON.stringify({ app_user_id: pgUserId })
            });

            if (!response.ok) {
                throw new Error('Failed to cancel subscription');
            }
        } catch (error) {
            setError('Failed to cancel subscription. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    return { cancelAllSubscriptions, loading, error };
}

export const useRevertSubscriptions = () => {
    const { sessionToken, pgUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const pgUserId = pgUser.id;

    const revertSubscriptions = async (currentlyCancelledSubscriptionId, scheduledSubscriptionId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`/api/v1/users/revert_stripe_subscription`, {
                method: 'POST',
                headers: await addCsrfHeader({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                }),
                body: JSON.stringify({ app_user_id: pgUserId, scheduled_subscription_id: scheduledSubscriptionId, current_subscription_id: currentlyCancelledSubscriptionId })
            });

            if (!response.ok) {
                throw new Error('Failed to revert subscription');
            }
        } catch (error) {
            setError('Failed to revert subscription. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    return { revertSubscriptions, loading, error };
}

export const useUncancelSubscription = () => {
    const { sessionToken, pgUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const pgUserId = pgUser.id;

    const uncancelSubscription = async (subscriptionId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`/api/v1/users/uncancel_stripe_subscription`, {
                method: 'POST',
                headers: await addCsrfHeader({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                }),
                body: JSON.stringify({ app_user_id: pgUserId, subscription_id: subscriptionId })
            });

            if (!response.ok) {
                throw new Error('Failed to uncancel subscription');
            }
        } catch (error) {
            setError('Failed to uncancel subscription. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    return { uncancelSubscription, loading, error };
}

export const usePaymentMethods = () => {
    const { sessionToken, pgUser } = useAuth();
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchPaymentMethods = async () => {
        if (!pgUser?.stripe_customer_id) {
            setPaymentMethods([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log('fetching payment methods from stripe with pgUser.stripe_customer_id:', pgUser.stripe_customer_id, 'and pgUser.id:', pgUser.id);
            const response = await fetch(`/api/v1/users/stripe_payment_methods`, {
                method: 'POST',
                headers: await addCsrfHeader({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                }),
                body: JSON.stringify({ app_user_id: pgUser.id })
            });

            if (!response.ok) {
                throw new Error('Failed to fetch payment methods');
            }

            const data = await response.json();
            setPaymentMethods(data);
        } catch (err) {
            setError('Failed to fetch payment methods. Please try again.');
            console.error('Error fetching payment methods:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPaymentMethods();
    }, [pgUser?.stripe_customer_id]);

    return { paymentMethods, loading, error, refetch: fetchPaymentMethods };
}

export const useStripePortal = () => {
    const { sessionToken, pgUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const redirectToPortal = async () => {
        if (!pgUser?.stripe_customer_id) {
            setError('No customer ID found');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/v1/users/create_stripe_customer_portal_session', {
                method: 'POST',
                headers: await addCsrfHeader({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                }),
                body: JSON.stringify({
                    app_user_id: pgUser.id
                })
            });

            if (!response.ok) {
                throw new Error('Failed to create portal session');
            }

            const { url } = await response.json();
            window.open(url, '_blank');
        } catch (err) {
            setError('Failed to redirect to payment portal. Please try again.');
            console.error('Error creating portal session:', err);
        } finally {
            setLoading(false);
        }
    };

    return { redirectToPortal, loading, error };
}

export const useDeleteAccount = () => {
    const { sessionToken, signOutUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const deleteAccount = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/v1/users/account', {
                method: 'DELETE',
                headers: await addCsrfHeader({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                })
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || 'Failed to delete account');
            }

            await signOutUser();
        } catch (err) {
            setError(err.message || 'Failed to delete account. Please try again.');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return { deleteAccount, loading, error };
}